import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Loader2, 
  MoreHorizontal, 
  Trash2, 
  Eye,
  Mail,
  Building2,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700', icon: Clock },
  { value: 'contacted', label: 'Contacted', color: 'bg-amber-100 text-amber-700', icon: Mail },
  { value: 'converted', label: 'Converted', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  { value: 'declined', label: 'Declined', color: 'bg-slate-100 text-slate-700', icon: XCircle }
];

const INTEREST_LABELS = {
  'lp_investment': 'LP Investment',
  'strategic_partnership': 'Strategic Partnership',
  'founder_pitch': 'Founder Pitch',
  'media_press': 'Media / Press',
  'other': 'Other'
};

const StatusBadge = ({ status }) => {
  const option = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  const Icon = option.icon;
  
  return (
    <Badge className={`${option.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {option.label}
    </Badge>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card className="metric-card">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">{title}</p>
          <p className="font-heading text-3xl font-light text-slate-900 mt-2">{value}</p>
        </div>
        <div className={`p-3 ${color} rounded-lg`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function InquiriesPage() {
  const { getAuthHeaders } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0, declined: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [inquiriesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/inquiries`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/inquiries/stats`, { headers: getAuthHeaders() })
      ]);
      setInquiries(inquiriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/inquiries/${inquiryId}/status?status=${newStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (inquiry) => {
    if (!window.confirm(`Delete inquiry from ${inquiry.name}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/inquiries/${inquiry.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Inquiry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredInquiries = statusFilter === 'all' 
    ? inquiries 
    : inquiries.filter(i => i.status === statusFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="inquiries-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="inquiries-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">LP Inquiries</h1>
        <p className="text-slate-500 mt-1">Manage and respond to investor inquiries</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Total" value={stats.total} icon={MessageSquare} color="bg-slate-600" />
        <StatCard title="New" value={stats.new} icon={Clock} color="bg-blue-500" />
        <StatCard title="Contacted" value={stats.contacted} icon={Mail} color="bg-amber-500" />
        <StatCard title="Converted" value={stats.converted} icon={UserCheck} color="bg-emerald-500" />
        <StatCard title="Declined" value={stats.declined} icon={XCircle} color="bg-slate-400" />
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inquiries</SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-slate-500">
              Showing {filteredInquiries.length} of {inquiries.length} inquiries
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Inquiries Table */}
      <Card data-testid="inquiries-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Contact</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Interest</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                    No inquiries found
                  </TableCell>
                </TableRow>
              ) : (
                filteredInquiries.map((inquiry) => (
                  <TableRow key={inquiry.id} data-testid={`inquiry-row-${inquiry.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{inquiry.name}</p>
                        {inquiry.company && (
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {inquiry.company}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <a 
                        href={`mailto:${inquiry.email}`}
                        className="text-sm text-[#1E3A5F] hover:underline flex items-center gap-1"
                      >
                        <Mail className="h-3 w-3" />
                        {inquiry.email}
                      </a>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {INTEREST_LABELS[inquiry.investment_interest] || 'Not specified'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={inquiry.status} 
                        onValueChange={(value) => handleStatusChange(inquiry.id, value)}
                      >
                        <SelectTrigger className="w-[140px] h-8" data-testid={`status-select-${inquiry.id}`}>
                          <StatusBadge status={inquiry.status} />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(s => (
                            <SelectItem key={s.value} value={s.value}>
                              <div className="flex items-center gap-2">
                                <s.icon className="h-3 w-3" />
                                {s.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(inquiry.created_at)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`actions-${inquiry.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedInquiry(inquiry);
                            setDetailsOpen(true);
                          }}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(inquiry)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg" data-testid="inquiry-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Inquiry Details</DialogTitle>
            <DialogDescription>
              Submitted {selectedInquiry && formatDate(selectedInquiry.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedInquiry && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Name</p>
                  <p className="font-medium text-slate-900">{selectedInquiry.name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Email</p>
                  <a 
                    href={`mailto:${selectedInquiry.email}`}
                    className="text-[#1E3A5F] hover:underline"
                  >
                    {selectedInquiry.email}
                  </a>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Company</p>
                  <p className="text-slate-900">{selectedInquiry.company || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Interest</p>
                  <Badge variant="secondary">
                    {INTEREST_LABELS[selectedInquiry.investment_interest] || 'Not specified'}
                  </Badge>
                </div>
              </div>
              
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Message</p>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedInquiry.message}</p>
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Status</p>
                <StatusBadge status={selectedInquiry.status} />
              </div>
            </div>
          )}

          <DialogFooter>
            {selectedInquiry && (
              <Button 
                variant="outline"
                onClick={() => window.open(`mailto:${selectedInquiry.email}?subject=Re: Your Inquiry to Berkshire Africa`)}
              >
                <Mail className="h-4 w-4 mr-2" />
                Reply via Email
              </Button>
            )}
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
